import {
  createContext,
  forwardRef,
  type ForwardedRef,
  type ReactElement,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Virtuoso,
  TableVirtuoso,
  type Components,
  type TableComponents,
  type ItemProps,
  type VirtuosoHandle,
  type TableVirtuosoHandle,
  type VirtuosoProps,
} from 'react-virtuoso';

import { trueFn } from '@frontend/utils';

import { type AnyHierarchyNode } from './models/HierarchyNode';

//iterator of nodes that are siblings and their depth
type ChildrenDepthPair<T extends AnyHierarchyNode> = [
  Iterator<T>, //iterator of nodes that are siblings
  T[], //parents of the children (ancestors starting with root)
];

// Id is needed for searching the tree for a given node
export type NodeWithId<T extends AnyHierarchyNode> = T & {
  id: string;
};

//NodeWithContext collects additional context about the node
//* this additional context can be useful for additional filtering or passing to the content renderer for that node
export type NodeWithContext<T extends AnyHierarchyNode> = {
  node: T;
  isExpanded: boolean;
  depth: number;
  indexInTreeView: number; //useful for zebra striping and other even/odd effects
  parents: T[]; //Note: root is parents[0] and if parents[0] is `undefined` then node is root.
  previous?: T; //not necessarily previous sibling. This is previous in expanded list
  following?: T; //not necessarily following sibling. This is following in expanded list
  passedPredicate?: boolean; // if predicate provided: true if passed predicate, false if not passed
};

export type NodeContentProps<T extends AnyHierarchyNode> =
  | NodeWithContext<T | FooterNode> & { toggleNodeExpanded?(): boolean };

export type TableOrList = 'table' | 'list';

//IMPORTANT: nodeContent must return a ReactElement for the item with a unique `key`
type NodeContentResultProps = { key: string };
type NodeContentResult = ReactElement<NodeContentResultProps> | null;

type DynamicHeightProps = {
  maxHeight: number;
  minHeight: number;
  itemHeightEstimate: number;
  padding?: number;
  additionalHeight?: number;
  excludeFooter?: boolean;
};

export type ExpandableNodeProps<
  T extends AnyHierarchyNode,
  U extends TableOrList,
> = {
  root: T;
  //css height to render data in
  height: string | number;
  //css width to render data in
  width: string | number;
  // object to manage maxHeight settings
  dynamicHeightProps?: DynamicHeightProps;
  // increaseViewportBy -- https://virtuoso.dev/virtuoso-api-reference/
  // artificially increase the viewport size, causing items to be rendered before outside of the viewport
  // causes the component to render more items than the necessary, but can help with slow loading content
  increaseViewportBy?: VirtuosoProps<unknown, unknown>['increaseViewportBy'];
  wrapperType?: U;
  components: U extends 'table'
    ? TableComponents<ItemPropsContext>
    : Components<ItemPropsContext>;
  nodeContent: (
    props: NodeContentProps<T | FooterNode>,
    index: number,
  ) => NodeContentResult;
  hideRoot?: boolean; //if we don't want to render the root, and start rendering its children, hideRoot is true
  expandAll?: boolean;
  predicate?: (node: T) => boolean;
  yieldFooterNode?: boolean;
  footerNodePadding?: boolean;
  //predicateSignal: Signal<T>;
};

function* descendantsAndSelf<T extends AnyHierarchyNode>(
  node: T,
): IterableIterator<T> {
  const stack: T[] = [node];
  let next: T | undefined;
  //eslint-disable-next-line no-cond-assign
  while ((next = stack.pop())) {
    yield next;
    for (const child of next.children ?? []) {
      stack.push(child);
    }
  }
}

//treeIter is a utility to iterate over the root node and its descendents and yield the nodes and additional context data
//* It uses a while loop and stack instead of using recursive function calls in order to eliminate risk of blowing the
//  functional call stack. As well, the while loop is faster than making function calls.
//* It is abstracted into this utility because when a predicate is provided, we need to iterate over the tree twice. The first
//  time is required to identify the nodes to filter/keep and which previous ancestors to show. The second pass is to render
//  the unfiltered nodes.
//* isRendered is used to apply the predicate filtering in addition to the isExpanded filtering
function* treeIter<T extends AnyHierarchyNode>(
  root: T,
  hideRoot = false,
  getIsExpanded = (node: T): node is Required<T> => !!node.children,
  isRendered: ((node: T) => node is Required<T>) | (() => boolean) = trueFn,
): IterableIterator<NodeWithContext<T>> {
  const crumbs = new WeakSet();
  //childrenStack is a stack of the iterators of children to yield. (depth first / LIFO sequence)
  const childrenStack: ChildrenDepthPair<T>[] = [
    [[root][Symbol.iterator](), []],
  ];
  let indexInTreeView = 0;
  let nextNodeWithContext: NodeWithContext<T> | undefined;
  let remainingChildren: ChildrenDepthPair<T> | undefined;
  let previous: T | undefined;
  //remainingChildren peeks() at entry on stack (which may change each iteration of while loop)
  //eslint-disable-next-line no-cond-assign
  while ((remainingChildren = childrenStack[childrenStack.length - 1])) {
    const [childrenIterator, parents]: ChildrenDepthPair<T> = remainingChildren;
    //take next child node from childrenIterator
    const [node] = childrenIterator as IterableIterator<T>;
    if (node) {
      //crumbs are to prevent circular walks
      if (crumbs.has(node)) {
        continue;
      } else {
        crumbs.add(node);
      }
      if (nextNodeWithContext) {
        //yield NodeWithContext
        //* yielding is one iteration behind because it needs to discover the following.
        nextNodeWithContext.following = node;
        yield nextNodeWithContext;
      }
      //If node is expanded, and there are children, push the children iterator, children depth and their parent to the stack
      let isExpanded = false;
      if (node.children && getIsExpanded(node) && isRendered(node)) {
        isExpanded = true;
        childrenStack.push([
          node.children[Symbol.iterator](),
          [...parents, node],
        ]);
      }
      if (node === root && hideRoot) {
        continue;
      }
      indexInTreeView++;
      //construct nextNodeWithContext to yield
      //* Note: following is not known yet... so waiting to yield
      nextNodeWithContext = {
        node,
        isExpanded,
        depth: hideRoot ? parents.length - 1 : parents.length,
        parents,
        ...(previous && { previous }),
        indexInTreeView,
      };
      previous = node;
    } else {
      childrenStack.pop(); //done with current children
    }
  }
  if (nextNodeWithContext) {
    //yield the final nodeContent (recall yielding is one iteration behind)
    yield nextNodeWithContext;
  }
}

// treeAncestors is a utility to iterate over the child node and its ancestors and yield the nodes
//* It uses a while loop and stack instead of using recursive function calls in order to eliminate risk of blowing the
//  functional call stack. As well, the while loop is faster than making function calls.
//  expandNode additionally expands the searchNode, set to false if you only want parents of the node to be expanded
function* treeAncestors<T extends AnyHierarchyNode>(
  root: NodeWithId<T>,
  searchNode: NodeWithId<T>,
  expandNode = true,
): IterableIterator<NodeWithId<T>> {
  const stack: { node: NodeWithId<T>; ancestors: NodeWithId<T>[] }[] = [
    { node: root, ancestors: [] },
  ];
  let matchedNodeAncestors: NodeWithId<T>[] = [];

  let nextNode: { node: NodeWithId<T>; ancestors: NodeWithId<T>[] } | undefined;

  while ((nextNode = stack.pop())) {
    const { node, ancestors } = nextNode;

    if (node.id === searchNode.id) {
      if (expandNode) {
        matchedNodeAncestors = [...ancestors, node];
      } else {
        matchedNodeAncestors = [...ancestors];
      }
      break;
    }

    for (const child of node.children ?? []) {
      stack.push({ node: child, ancestors: [...ancestors, node] });
    }
  }

  for (const nextNode of matchedNodeAncestors) {
    yield nextNode;
  }
}

class FooterNode implements AnyHierarchyNode {
  public readonly children: undefined;
  public readonly hiddenCount: number;
  public readonly totalCount: number;
  constructor({
    hiddenCount,
    totalCount,
  }: {
    hiddenCount: number;
    totalCount: number;
  }) {
    this.hiddenCount = hiddenCount;
    this.totalCount = totalCount;
  }
}
export const isFooterNode = (n: unknown): n is FooterNode =>
  n instanceof FooterNode;
export type { FooterNode };

type ItemPropsContext = Omit<ItemProps, 'children'>;
export const RowItemContext = createContext<Partial<ItemPropsContext>>({});

//About TableRow (a thin wrapper that allows us to mix in props from TableVirtuoso and props we want to add)
//* When used to expand nodes of a table, `nodeContent` returns a `<tr>`-like element that may have props on it such as a click handler
//* But TableVirtuoso wants to add props to the `<tr>`-like element too... OOTB, TableVirtuoso seems to be designed to not give the `itemContent`
//  the flexibility to add props to the wrapped `<tr>`-like element (ie the `TableRow` prop in TableComponents).
//* The following `TableRow` let's us mix the props together so we get the props TableVirtuoso needs and so we get the props we want such
//  as a click handler
const TableRow = (
  //It is expected that children is a `<tr>`-like element
  {
    children: tr,
    ...itemProps
  }: { children: NodeContentResult } & ItemPropsContext,
) =>
  tr ? (
    <RowItemContext.Provider key={tr.props.key} value={itemProps}>
      {tr}
    </RowItemContext.Provider>
  ) : null;

export type ExpandableNodeHandle<
  T extends AnyHierarchyNode,
  U extends TableOrList,
> = {
  virtuoso: U extends 'table' ? TableVirtuosoHandle : VirtuosoHandle;
  expandNode(node: T): void;
  expandNodeRecursive(node: T): void;
  expandParentsToRoot(node: NodeWithId<T>, root: NodeWithId<T>): void;
  collapseNode(node: T): void;
  collapseNodeRecursive(node: T): void;
  expandOrCollapseWithPredicate(
    predicate: (node: NodeWithId<T>) => boolean,
    root: NodeWithId<T>,
    expandOrCollapseFunc: (node: NodeWithId<T>, root: NodeWithId<T>) => void,
    prepFunction: null | (() => void),
  ): void;
  toggleNodeExpanded(node: T): boolean;
  getIsExpanded(node: T): node is Required<T>;
};

const ExpandableNode = forwardRef(
  <T extends AnyHierarchyNode, U extends TableOrList>(
    {
      root,
      wrapperType = 'table' as U,
      components,
      nodeContent,
      hideRoot,
      expandAll,
      height,
      width,
      dynamicHeightProps,
      increaseViewportBy,
      predicate,
      yieldFooterNode,
      footerNodePadding,
    }: ExpandableNodeProps<T, U>,
    ref: ForwardedRef<ExpandableNodeHandle<T, U>>,
  ) => {
    const virtuoso: Ref<
      U extends 'table' ? TableVirtuosoHandle : VirtuosoHandle
    > = useRef<U extends 'table' ? TableVirtuosoHandle : VirtuosoHandle>(null);
    //Nodes that are expanded are tracked in WeakSet so that expanded state is automatically disposed of when node is disposed
    //The WeakSet is wrapped in a tuple so that we don't have to re-write the WeakSet every time the node's expanded state is toggled
    //Instead a new tuple is set. This tuple wraps the same WeakSet, and the new tuple signals a re-render for the component
    //Use toggleNodeExpanded() and getIsExpanded() to make working with the WeakSet easier.
    const [_isExpandedSetTuple, _setIsExpandedTupleSet] = useState<
      [WeakSet<T>]
    >([new WeakSet()]);

    const [totalListHeight, setTotalListHeight] = useState<number>(0);

    const toggleNodeExpanded = useCallback(
      (node: T): boolean => {
        const isExpandedSet = _isExpandedSetTuple[0];
        const isExpanded = isExpandedSet.has(node);
        if (isExpanded) {
          isExpandedSet.delete(node);
        } else {
          isExpandedSet.add(node);
        }
        _setIsExpandedTupleSet([isExpandedSet]);
        return !isExpanded;
      },
      [_isExpandedSetTuple],
    );

    const expandNode = useCallback(
      (node: T): void => {
        const isExpandedSet = _isExpandedSetTuple[0];
        const isExpanded = isExpandedSet.has(node);
        if (!isExpanded) {
          isExpandedSet.add(node);
          _setIsExpandedTupleSet([isExpandedSet]);
        }
      },
      [_isExpandedSetTuple],
    );

    const expandNodeRecursive = useCallback(
      (parentNode: T): void => {
        const isExpandedSet = _isExpandedSetTuple[0];
        let changed = false;
        for (const node of descendantsAndSelf(parentNode)) {
          if (node.children && !isExpandedSet.has(node)) {
            isExpandedSet.add(node);
            changed = true;
          }
        }
        if (changed) {
          _setIsExpandedTupleSet([isExpandedSet]);
        }
      },
      [_isExpandedSetTuple],
    );

    // Requires nodes of type T to have a unique id (as per type NodeWithId<T>)
    // This id is used to match nodes
    // Must pass in new ref to root, in case root changes with new nodes
    const expandParentsToRoot = useCallback(
      (node: NodeWithId<T>, root: NodeWithId<T>): void => {
        const isExpandedSet = _isExpandedSetTuple[0];

        let changed = false;
        for (const nextNode of treeAncestors(root as NodeWithId<T>, node)) {
          isExpandedSet.add(nextNode);
          changed = true;
        }
        if (changed) {
          _setIsExpandedTupleSet([isExpandedSet]);
        }
      },
      [_isExpandedSetTuple],
    );

    /**
     * Function that allows you to filter for nodes in the list of data, and then
     * perform an expand or collapse function on them that is passed in.
     * It needs to be a NodeWithId because you may want to have the expandParentsToRoot
     * be the expandOrCollapseFunc, which requires a NodeWithId.
     * The root will be the node that we traverse up and down.
     * The prepFunction can be passed to optionally prepare the expanded state
     * prior to expandOrCollapseFunc being executed on the nods matching the
     * predicate.
     */
    const expandOrCollapseWithPredicate = useCallback(
      (
        predicate: (node: NodeWithId<T>) => boolean,
        root: NodeWithId<T>,
        expandOrCollapseFunc: (
          node: NodeWithId<T>,
          root: NodeWithId<T>,
        ) => void,
        prepFunction: null | (() => void) = null,
      ) => {
        if (prepFunction) prepFunction();

        for (const node of descendantsAndSelf(root)) {
          if (predicate(node)) {
            expandOrCollapseFunc(node, root);
          }
        }
      },
      [],
    );

    const collapseNode = useCallback(
      (node: T): void => {
        const isExpandedSet = _isExpandedSetTuple[0];
        const isExpanded = isExpandedSet.has(node);
        if (isExpanded) {
          isExpandedSet.delete(node);
          _setIsExpandedTupleSet([isExpandedSet]);
        }
      },
      [_isExpandedSetTuple],
    );

    const collapseNodeRecursive = useCallback(
      (parentNode: T): void => {
        const isExpandedSet = _isExpandedSetTuple[0];
        let changed = false;
        for (const node of descendantsAndSelf(parentNode)) {
          if (node.children && isExpandedSet.has(node)) {
            isExpandedSet.delete(node);
            changed = true;
          }
        }
        if (changed) {
          _setIsExpandedTupleSet([isExpandedSet]);
        }
      },
      [_isExpandedSetTuple],
    );

    const getIsExpanded = useCallback(
      (node: T): node is Required<T> => _isExpandedSetTuple[0].has(node),
      [_isExpandedSetTuple],
    );

    //expand all/collapse all when expandAll's value toggles
    useEffect(() => {
      const isExpandedSet = _isExpandedSetTuple[0];
      for (const node of descendantsAndSelf(root)) {
        if (node.children) {
          if (expandAll) {
            isExpandedSet.add(node);
          } else {
            isExpandedSet.delete(node); //can't clear with Weakset... so delete separately
          }
        }
      }
      _setIsExpandedTupleSet([isExpandedSet]);
      //Important: Don't add _isExpandedSetTuple to useEffect deps, or everything will be stuck expanded.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [root, expandAll]);

    //yield nodeContent for visible rows depth first. Using Stack so we don't blow function call stack.
    const data: (NodeContentProps<T> | NodeContentProps<FooterNode>)[] =
      useMemo(() => {
        //Note: When a predicate is provided, an additional iterations over the nodes is required. The first pass is necessary
        //to mark the nodes and ancestors that should be expanded. In particular ancestors of shown nodes should be unfolded temporarily.
        let totalCount = 0;
        let hiddenCount = 0;
        const tempNodesToShow = new Set();
        // This Set keeps track of which nodes themselves pass the predicate. It allows for separation between matched nodes and parents of matched nodes
        const tempNodesPassingPredicate = new Set();
        if (predicate) {
          for (const { node, parents } of treeIter(root, false)) {
            totalCount++;
            if (predicate(node)) {
              tempNodesToShow.add(node);
              tempNodesPassingPredicate.add(node);
              //temporarily mark the ancestors as expanded so the nodes satisfying predicate are visible
              for (const parent of parents) {
                tempNodesToShow.add(parent);
              }
            }
          }
          // hiddenCount should not include rendered parent nodes that don't pass predicate themselves
          hiddenCount = totalCount - tempNodesToShow.size;
        }
        return Array.from(
          (function* () {
            //Data for main rows
            for (const context of treeIter(
              root,
              hideRoot,
              getIsExpanded,
              predicate
                ? (node: T): node is Required<T> => tempNodesToShow.has(node)
                : undefined,
            )) {
              const { node } = context;
              if (node) {
                if (!predicate || tempNodesToShow.has(node)) {
                  if (tempNodesPassingPredicate.has(node)) {
                    context.passedPredicate = true;
                  }
                  yield {
                    ...context,
                    ...(node.children && {
                      toggleNodeExpanded: () => toggleNodeExpanded(node),
                    }),
                  };
                }
              }
            }
            //If applicable, yield Footer node (footer node is rendered inside Virtuoso in last position)
            if (yieldFooterNode && (hiddenCount || footerNodePadding)) {
              yield {
                node: new FooterNode({ hiddenCount, totalCount }),
                isExpanded: false,
                depth: 0,
                parents: [],
              };
            }
          })(),
        );
      }, [
        root,
        hideRoot,
        predicate,
        getIsExpanded,
        toggleNodeExpanded,
        yieldFooterNode,
        footerNodePadding,
      ]);

    const VirtuosoComponent =
      !wrapperType || wrapperType === 'table' ? TableVirtuoso : Virtuoso;

    if (wrapperType === 'table') {
      //TableRow is a valid component for a row. The types are problematic in virtuoso, and so pragmatic solution is to cast as `any`
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      (components as TableComponents<ItemPropsContext>).TableRow =
        TableRow as any;
    }

    if (ref && typeof ref === 'object' && virtuoso.current && !ref.current) {
      ref.current = {
        expandNode,
        expandNodeRecursive,
        expandParentsToRoot,
        collapseNode,
        collapseNodeRecursive,
        expandOrCollapseWithPredicate,
        toggleNodeExpanded,
        getIsExpanded,
        virtuoso: virtuoso.current,
      };
    }

    const _height = useMemo(() => {
      if (dynamicHeightProps) {
        const {
          maxHeight,
          minHeight,
          itemHeightEstimate,
          excludeFooter,
          padding,
          additionalHeight,
        } = dynamicHeightProps;
        let estimatedHeight =
          itemHeightEstimate * (excludeFooter ? data.length - 1 : data.length) +
          (padding ?? 0);
        if (estimatedHeight < totalListHeight) {
          estimatedHeight = totalListHeight + (additionalHeight ?? 0);
        }
        if (estimatedHeight >= maxHeight) {
          return maxHeight;
        } else if (estimatedHeight <= minHeight) {
          return minHeight;
        } else {
          return estimatedHeight;
        }
      }
      return height;
    }, [data.length, dynamicHeightProps, height, totalListHeight]);

    return (
      <VirtuosoComponent
        style={{ height: _height, width }}
        increaseViewportBy={increaseViewportBy ?? 0}
        components={components}
        totalListHeightChanged={setTotalListHeight}
        data={data}
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={virtuoso as any}
        itemContent={(index, nextProps) => nodeContent(nextProps, index)}
      />
    );
  },
);
ExpandableNode.displayName = 'ExpandlableNode';
export { ExpandableNode };
